import React from "react";

import cn from "classnames";

import { MenuCore } from "./MenuCore";
import Base from "./components/Base/MenuBase";
import { boxForwardRef } from "../../../../helpers/common/Box/Box";
import { Popup } from "../../../../helpers/common/Popup/Popup";
import { useDropdownStore } from "../../store/store";
import { createDisplayNameForDropdownElement } from "../../utils/getDisplayName";
import { createTestIdForDropdownElement } from "../../utils/getTestId";

import type { MenuProps } from "./types";
import type { BoxComponentWithDefault } from "../../../../helpers/common/Box/Box";

import classes from "./DropdownMenu.styl";

const MENU_POPUP_TEST_ID = createTestIdForDropdownElement("menu-popup");

/**
 * Компонент-обёртка с логикой показа выпадашки.
 * Вся логика по навигации вынесена в MenuCore.
 */
export const DropdownMenu = boxForwardRef<
    BoxComponentWithDefault<typeof Base, MenuProps>
>(
    (
        {
            as = Base,
            inline = true,
            side,
            alignment,
            portalClassName,
            getContainer,
            adjustmentLocked = true,
            children,
            customVarsStyles,
            customWidthValue,
            ...props
        },
        ref,
    ) => {
        const { isOpen, buttonRef, level } = useDropdownStore();

        const portalProps = !inline
            ? {
                  inline: false as const,
                  portalClassName: portalClassName!,
                  getContainer: getContainer!,
              }
            : {};

        const popupClasses = cn(
            classes["custom-vars-styles"],
            customVarsStyles,
        );

        return (
            <Popup
                {...portalProps}
                isOpen={isOpen}
                targetRef={buttonRef}
                side={side}
                alignment={alignment}
                adjustmentLocked={adjustmentLocked}
                zIndex={level}
                customVarsStyles={popupClasses}
                data-tsid={MENU_POPUP_TEST_ID}
            >
                <MenuCore
                    {...props}
                    customWidthValue={customWidthValue}
                    as={as}
                    withGlobalFocusTrap={inline}
                    customVarsStyles={customVarsStyles}
                    ref={ref}
                >
                    {children}
                </MenuCore>
            </Popup>
        );
    },
);

DropdownMenu.displayName = createDisplayNameForDropdownElement("Menu");

export default DropdownMenu;
